import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Trans, useTranslation } from 'react-i18next';

import {
  cancelWithdrawRequest,
  submitWithdrawRequest,
  type MyProfileDTO,
} from '@/api/adapters/my-profile';
import { useModalStore } from '@/app/store/modal.store';
import { toast } from '@/app/store/toast.store';
import { MY_PROFILE_QUERY_KEY } from '@/shared/hooks/use-my-profile';

// Display dates in the same YYYY.MM.DD style used by [MyInfoCard.tsx].
const formatIsoToDot = (iso: string): string =>
  iso.slice(0, 10).replace(/-/g, '.');

interface WithdrawCardProps {
  profile: MyProfileDTO;
}

export function WithdrawCard({ profile }: WithdrawCardProps) {
  const { t } = useTranslation('myProfile');
  const queryClient = useQueryClient();
  const openModal = useModalStore((s) => s.open);
  const closeModal = useModalStore((s) => s.close);
  const [isBusy, setIsBusy] = useState(false);

  const pending = profile.withdrawRequest;

  const handleRequest = useCallback(async () => {
    setIsBusy(true);
    try {
      const result = await submitWithdrawRequest();
      queryClient.setQueryData<MyProfileDTO | undefined>(
        MY_PROFILE_QUERY_KEY,
        (prev) => (prev ? { ...prev, withdrawRequest: result } : prev),
      );
      queryClient.invalidateQueries({ queryKey: MY_PROFILE_QUERY_KEY });
      toast.success(t('cards.withdraw.messages.requestSuccess'));
    } catch {
      toast.error(t('cards.withdraw.errors.requestFailure'));
    } finally {
      setIsBusy(false);
    }
  }, [queryClient, t]);

  const handleCancel = useCallback(async () => {
    setIsBusy(true);
    try {
      await cancelWithdrawRequest();
      queryClient.setQueryData<MyProfileDTO | undefined>(
        MY_PROFILE_QUERY_KEY,
        (prev) => (prev ? { ...prev, withdrawRequest: null } : prev),
      );
      queryClient.invalidateQueries({ queryKey: MY_PROFILE_QUERY_KEY });
      toast.success(t('cards.withdraw.messages.cancelSuccess'));
    } catch {
      toast.error(t('cards.withdraw.errors.cancelFailure'));
    } finally {
      setIsBusy(false);
    }
  }, [queryClient, t]);

  const openRequestConfirm = useCallback(() => {
    openModal({
      title: t('cards.withdraw.confirmRequest.title'),
      // <Trans> renders the i18n string with <1>…</1> as <strong>, matching the
      // prototype's bold emphasis on "permanently blocked" / "영구 차단".
      body: (
        <p className="modal__message">
          <Trans
            i18nKey="cards.withdraw.confirmRequest.body"
            ns="myProfile"
            components={{ 1: <strong /> }}
          />
        </p>
      ),
      showCancel: true,
      confirmLabel: t('cards.withdraw.confirmRequest.confirm'),
      confirmDanger: true,
      onConfirm: () => {
        closeModal();
        void handleRequest();
      },
      onCancel: closeModal,
    });
  }, [openModal, closeModal, handleRequest, t]);

  const openCancelConfirm = useCallback(() => {
    openModal({
      title: t('cards.withdraw.confirmCancel.title'),
      body: t('cards.withdraw.confirmCancel.body'),
      showCancel: true,
      confirmLabel: t('cards.withdraw.confirmCancel.confirm'),
      onConfirm: () => {
        closeModal();
        void handleCancel();
      },
      onCancel: closeModal,
    });
  }, [openModal, closeModal, handleCancel, t]);

  return (
    <div className="card profile-card">
      <div className="card__header">
        <h3 className="card__title">{t('cards.withdraw.title')}</h3>
      </div>
      <div className="card__body">
        <p className="form-field__hint">{t('cards.withdraw.hint')}</p>
        {pending ? (
          <div style={{ marginTop: 8 }}>
            <div
              style={{
                color: 'var(--error)',
                fontWeight: 600,
                marginBottom: 8,
              }}
            >
              {t('cards.withdraw.pendingLabel', {
                date: formatIsoToDot(pending.requestedAt),
              })}
            </div>
            <button
              type="button"
              className="btn btn--secondary btn--sm"
              onClick={openCancelConfirm}
              disabled={isBusy}
              aria-busy={isBusy}
            >
              {t('cards.withdraw.actions.cancel')}
            </button>
          </div>
        ) : (
          <div style={{ marginTop: 8 }}>
            <button
              type="button"
              className="btn btn--danger"
              onClick={openRequestConfirm}
              disabled={isBusy}
              aria-busy={isBusy}
            >
              {t('cards.withdraw.actions.request')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
